import {
  FluidTheme,
  FluidThemeInterface,
  FluidThemeVariant,
} from '@lmig/fluid-core';
import { Component, ComponentInterface, Element, h, Prop } from '@stencil/core';

import { INVALID_CONTENT_IMPLEMENTATION } from '../../constants/constants';
import { Utils } from '../../utils/utils';

/**
 * @displayName Page Content Container
 *
 * @internal
 *
 * @excludeTag
 */
@Component({
  tag: 'fluid-content',
  styleUrl: 'fluid-content.scss',
  shadow: true,
})
export class FluidContent implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidContentElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Sets the background color of the content container.
   */
  @Prop({ mutable: true }) themeVariant: FluidThemeVariant =
    FluidThemeVariant.DEFAULT;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.validateImplementation();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Validates that FLUID Content was implemented correctly via FLUID
   * Page. If not, outputs console warning stating that FLUID Content
   * should not be used independently of FLUID Page.
   */
  validateImplementation(): void {
    if (this.mainInstance) return;
    Utils.consoleWarn(this.host, INVALID_CONTENT_IMPLEMENTATION, true);
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const RootTag = this.mainInstance ? 'main' : 'div';

    return (
      <RootTag
        {...(this.mainInstance && { id: 'fluid-main' })}
        class={{
          [`fluid-content theme-${this.theme}`]: true,
          [`variant-${this.themeVariant}`]: true,
        }}>
        <slot />
      </RootTag>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * If true, signifies that component was implemented properly via FLUID Page.
   * If false, component was implemented incorrectly & console warning will
   * be displayed.
   * @internal
   */
  @Prop({ mutable: true }) mainInstance = false;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "hasSidebar" is now deprecated. The Prop is unnecessary,
   * as FLUID Page handles hardware acceleration for Sidebar. -- Indicates
   * that FLUID Page contains one or more Sidebars. Enables hardware
   * acceleration for Sidebar expand/collapse transitions.
   * @deprecated
   */
  @Prop({ mutable: true }) hasSidebar = false;
}
